/**
 * The « insufficient credits » refusal, read the one way (`docs/plans/billing-ui.md` § Wave 1, 1.4
 * and 1.5).
 *
 * A `402 credits_exhausted` is the one refusal a screen can act on with money: nothing was started
 * and nothing was debited, and topping up is the remedy. So it is never a toast: it opens one modal
 * (`modals/CreditsExhaustedModal.vue`), and what a caller knows about the act — what it costs, and
 * whether it may be run again — joins it there.
 *
 * Pure, so the store and the subtitles' paths read one answer and a spec pins it.
 */
import { ApiError, CREDITS_EXHAUSTED } from '@/api/client'

/** Whether a failure is the prepaid balance refusing the act. Branches on the code, never on the message. */
export function isCreditsExhausted(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 402 && error.code === CREDITS_EXHAUSTED
}

/**
 * What a caller tells the modal about the act that was refused. Every field is optional: the
 * client's own handler reports the bare refusal, and the caller that knows more adds to it.
 *
 * **The body carries no figure** — only an English sentence (`backend/crates/bin/api/src/error.rs`,
 * `From<ChargeError>`), which nothing branches on. So the cost is the caller's own estimate, and the
 * balance is read from the wallet.
 */
export interface CreditsRefusal {
  /** What the act costs, in cents excluding tax, when the caller can say. */
  costCents?: number | null
  /** What the cost line names — « Sous-titres · 12 min commencées ». Already translated. */
  label?: string
  /**
   * Runs the act again once the credit has landed. **Opt-in, and only for an act the caller knows
   * is safe to repeat** — the client never replays a request on its own.
   */
  retry?: () => Promise<unknown>
}

/**
 * One modal for every refusal that arrives while it is open: a run of several languages answers
 * one 402 per language, and the client's handler reports each before the caller adds its cost and
 * its retry. The later word wins field by field, and a field nobody restated is kept.
 */
export function mergeRefusal(current: CreditsRefusal | null, next: CreditsRefusal): CreditsRefusal {
  return {
    costCents: next.costCents ?? current?.costCents ?? null,
    label: next.label || current?.label || '',
    retry: next.retry ?? current?.retry,
  }
}

/** The fields of a roster row and of a role this reading needs. */
export interface RosterMember {
  name?: string | null
  email: string
  role: string
}

export interface RosterRole {
  key: string
  permissions: string[]
}

/** The permission that tops a wallet up. */
export const BILLING_MANAGE = 'tenancy.billing.manage'

/**
 * Who a member may ask to top up: the members whose role declares `tenancy.billing.manage`.
 *
 * Both reads are open to every member (`GET /orgs/current/members` and `…/roles`), so a member who
 * cannot top up is told whom to ask rather than « an administrator ». The name, or the address when
 * the account gave none; at most `limit`, the rest being a count the caller words.
 */
export function billingManagersOf(
  members: readonly RosterMember[],
  roles: readonly RosterRole[],
  limit = 3,
): { names: string[]; more: number } {
  const managing = new Set(roles.filter((role) => role.permissions.includes(BILLING_MANAGE)).map((role) => role.key))
  const names = members.filter((member) => managing.has(member.role)).map((member) => member.name || member.email)
  return { names: names.slice(0, limit), more: Math.max(0, names.length - limit) }
}
